import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import {
  CameraIcon,
  CrossCircledIcon,
  Pencil1Icon,
  PlusIcon,
} from "@radix-ui/react-icons";
import _ from "lodash";
import { useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import resizeImageFromDataUrl from "src/lib/resizeImage";
import { addEvent } from "src/lib/slices/alcoholSlice";
import { selectEvent } from "src/lib/slices/drinkSelectionSlice";
import { v4 as uuidv4 } from "uuid";
import AddInPastButton from "../addInPastButton";
import Image from "../Image";
import PhotoPreview from "../PhotoPreview";
import { Button, ButtonStack } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import CustomEventDrawer from "./CustomEventDrawer";

export const events = [
  {
    type: "vomit",
    name: "Donate toaletě",
    icon: "vomit.svg",
  },
  {
    type: "turn-off",
    name: "Ztráta připojení k realitě",
    icon: "turn-off.svg",
  },
  {
    type: "sleep",
    name: "Šlofík",
    icon: "sleep.svg",
  },
  {
    type: "run",
    name: "Olympijský rekord",
    icon: "run.svg",
  },
  {
    type: "walk",
    name: "Kyslíková terapie",
    icon: "walk.svg",
  },
];

export default function EventSelector() {
  const dispatch = useDispatch();

  const selectedEvent = useSelector(
    (state: any) => state.drinkSelection.eventType
  );

  const customEvent = useSelector(
    (state: any) => state.drinkSelection.customEvent
  );

  const photoRef = useRef<HTMLInputElement>(null);
  const [photoData, setPhotoData] = useState<string | null>(null);

  const locationState = useSelector((state: any) => state.location);

  const getLatLong = () => {
    const lastLocationUpdate = locationState.lastUpdate;

    if (new Date().getTime() - lastLocationUpdate < 10 * 60 * 1000) {
      return {
        latitude: locationState.latitude,
        longitude: locationState.longitude,
      };
    }

    return {};
  };

  const add = ({ date }: { date?: number }) => {
    const { latitude, longitude } = getLatLong();
    console.log(date);
    dispatch(
      addEvent({
        id: uuidv4(),
        date: date ?? new Date().getTime(),
        type: selectedEvent,
        photoData,
        customName: selectedEvent === "custom" ? customEvent.text : undefined,
        latitude,
        longitude,
      })
    );

    setPhotoData(null);
    dispatch(selectEvent(undefined));

    toast("Událost byla přidána");

    if (Capacitor.isNativePlatform())
      (async () => {
        Haptics.impact({ style: ImpactStyle.Light });
        _.delay(() => Haptics.impact({ style: ImpactStyle.Light }), 250);
      })();
  };

  const CustomEventWrapper = useCallback(
    ({ children }) => {
      if (selectedEvent === "custom" || !customEvent.text) {
        return <CustomEventDrawer>{children}</CustomEventDrawer>;
      }

      return (
        <div
          onClick={() => {
            dispatch(selectEvent("custom"));
          }}
        >
          {children}
        </div>
      );
    },
    [customEvent.text, dispatch, selectedEvent]
  );

  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);

  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(5rem, 1fr))",
          gap: "0.5rem",
          padding: "0 0.5rem",
        }}
      >
        {events.map((event) => (
          <Card
            key={event.type}
            className="p-2"
            style={{
              backgroundColor:
                selectedEvent === event.type
                  ? "hsl(var(--primary))"
                  : undefined,
              color:
                selectedEvent === event.type
                  ? "hsl(var(--primary-foreground))"
                  : undefined,
              cursor: "pointer",
            }}
            onClick={() => {
              dispatch(selectEvent(event.type));
            }}
          >
            <CardContent className="py-2 px-0">
              <img
                src={`/event-icons/${event.icon}`}
                alt=""
                className="h-12 mx-auto"
              />
            </CardContent>
            <CardHeader
              style={{
                textAlign: "center",
                paddingBottom: 0,
                padding: "0",
              }}
              className="text-xs font-semibold"
            >
              {event.name}
            </CardHeader>
          </Card>
        ))}
        <CustomEventWrapper>
          <Card
            className="p-2 h-full"
            style={{
              backgroundColor:
                selectedEvent === "custom" ? "hsl(var(--primary))" : undefined,
              color:
                selectedEvent === "custom"
                  ? "hsl(var(--primary-foreground))"
                  : undefined,
              cursor: "pointer",
            }}
          >
            <CardContent className="py-2 px-0">
              {customEvent.text ? (
                <Pencil1Icon className="h-12 mx-auto" />
              ) : (
                <PlusIcon className="h-12 mx-auto" />
              )}
            </CardContent>
            <CardHeader
              style={{
                textAlign: "center",
                paddingBottom: 0,
                padding: "0",
              }}
              className="text-xs font-semibold"
            >
              {customEvent.text || "Vlastní"}
            </CardHeader>
          </Card>
        </CustomEventWrapper>
      </div>
      <div
        style={{
          paddingTop: "0.5rem",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          gap: "0.5rem",
          position: "relative",
        }}
      >
        <input
          type="file"
          accept="image/png, image/jpeg"
          name="photo"
          id="photo"
          ref={photoRef}
          style={{ display: "none" }}
          onChange={(event) => {
            event.preventDefault();
            const { files } = event.target;
            if (!files?.[0]) return;
            const reader = new FileReader();
            reader.onloadend = async () => {
              const dataUrl = await resizeImageFromDataUrl({
                dataUrl: reader.result as string,
              });
              if (!dataUrl) return;
              setPhotoData(dataUrl);
            };
            reader.readAsDataURL(files[0]);
          }}
        />
        {photoData && (
          <Button
            size="icon"
            variant="destructive"
            onClick={() => {
              setPhotoData(null);
            }}
          >
            <CameraIcon />
            <CrossCircledIcon />
          </Button>
        )}
        {!photoData && (
          <Button
            size="icon"
            className="absolute right-4"
            onClick={() => {
              if (Capacitor.isNativePlatform()) {
                Camera.getPhoto({
                  quality: 100,
                  allowEditing: false,
                  resultType: CameraResultType.DataUrl,
                  source: CameraSource.Camera,
                })
                  .then(async (photo) => {
                    const dataUrl = await resizeImageFromDataUrl({
                      dataUrl: photo.dataUrl as string,
                    });
                    setPhotoData(dataUrl);
                  })
                  .catch(() => {});
              } else {
                photoRef.current?.click();
              }
            }}
          >
            <CameraIcon />
            <PlusIcon />
          </Button>
        )}

        <ButtonStack disabled={!selectedEvent}>
          <AddInPastButton
            actionLabel="Přidat"
            onSubmit={add}
            disabled={!selectedEvent}
          />
          <Button
            onClick={() => add({})}
            stack="right"
            disabled={!selectedEvent}
          >
            Přidat
          </Button>
        </ButtonStack>

        {photoData && (
          <Image
            style={{
              position: "absolute",
              right: "1rem",
              height: "24vw",
              width: "16vw",
              objectFit: "cover",
              rotate: "25deg",
              cursor: "pointer",
              borderRadius: "0.5rem",
            }}
            src={photoData}
            alt="Fotka uživatele"
            onClick={() => setPhotoPreviewOpen(true)}
          />
        )}
      </div>
      <PhotoPreview
        setOpen={setPhotoPreviewOpen}
        open={photoPreviewOpen}
        imgUrl={photoData}
        alt="Fotka uživatele"
      />
    </>
  );
}
